import { ResourceManagementEndpoints } from "@app/shared/data-access/src/lib/resource-management/types/resource-management-endpoints";
import {
  HardFormFieldTypeEnum,
  HardTableColumnTypeEnum,
  IHardFormField,
  IHardFormSelectParams,
  IHardTable,
} from "@app/shared/ui-lib/src/lib";
import { IBaseConfig, IFormFieldFilter } from "@models/form/base-config";
import {
  IProperty,
  IPropertyEnumValue,
} from "@app/shared/data-access/src/lib/resource-management/models/property";
import { LangEnum } from "@models/interface-management/localization";
import { TranslationFieldPartialValidator } from "@static/fields/fields-constant";
import { FormGroup, Validators } from "@angular/forms";

export class PropertyConfig implements IBaseConfig {
  version = 1;
  dicType = ResourceManagementEndpoints.Property;
  isCrudByModal = true;

  fields: IHardFormField[] = [
    {
      name: "name",
      params: {
        direction: "row",
        fields: TranslationFieldPartialValidator(
          "dicTable.name",
          HardFormFieldTypeEnum.Input,
          [LangEnum.RU],
        ),
      },
    },
    {
      params: {
        direction: "row",
        templateColumns: "1fr 1fr",
        fields: [
          {
            title: "dicTable.unit",
            name: "unit",
            type: HardFormFieldTypeEnum.Dictionary,
            params: {
              dicType: "api/v1/" + ResourceManagementEndpoints.MeasurementUnit,
              fieldNames: ["displayName"],
              validators: [Validators.required],
            },
          },
          {
            title: "dicTable.dataType",
            name: "dataType",
            type: HardFormFieldTypeEnum.Dictionary,
            params: {
              dicType: "api/v1/" + ResourceManagementEndpoints.PropertyDataType,
              validators: [Validators.required],
            },
          },
        ],
      },
    },
    {
      name: "enumValues",
      title: "dicTable.allowedValues",
      type: HardFormFieldTypeEnum.Array,
      params: {
        conditionalFields: ["dataType", "dataTypeId"],
        conditionalCallback: (
          data: IProperty,
          config: IHardFormField,
          form: FormGroup,
        ) => {
          if (form.get("dataType")?.value?.code === "ENUM") {
            config.params.hide = false;
          } else {
            config.params.hide = true;
            form.get("enumValues").setValue(null, { emitEvent: false });
          }
        },
      },
    },
  ];

  saveCallback(data: IProperty): IProperty {
    return {
      id: data?.id,
      name: data.name,
      dataTypeId: data?.dataType?.id,
      unitId: data?.unit?.id,
      enumValues:
        ((data?.enumValues as IPropertyEnumValue[])?.map(
          (value) => value?.value,
        ) as string[]) ?? [],
    };
  }

  filterTemplateColumns = "1fr 200px 220px";

  filterFields: IFormFieldFilter<IHardFormSelectParams>[] = [
    {
      name: "searchTerm",
      type: HardFormFieldTypeEnum.Input,
      placeholder: "base.search",
      outside: true,
    },
    {
      name: "dataTypes",
      type: HardFormFieldTypeEnum.Dictionary,
      placeholder: "dicTable.allDataTypes",
      outside: true,
      params: {
        dicType: "api/v1/" + ResourceManagementEndpoints.PropertyDataType,
      },
    },
    {
      name: "units",
      type: HardFormFieldTypeEnum.Dictionary,
      placeholder: "dicTable.allUnits",
      outside: true,
      params: {
        dicType: "api/v1/" + ResourceManagementEndpoints.MeasurementUnit,
        fieldNames: ["displayName"],
      },
    },
  ];

  tableConfig: IHardTable<IProperty[]> = {
    showDefaultActions: true,
    tableColumns: "44px repeat(5,auto)",
    columns: [
      {
        title: "№",
        name: "index",
        type: HardTableColumnTypeEnum.Index,
      },
      {
        title: "dicTable.name",
        name: "name",
        type: HardTableColumnTypeEnum.Translate,
      },
      {
        title: "dicTable.dataType",
        name: "name",
        type: HardTableColumnTypeEnum.Parent,
        parent: "dataType",
      },
      {
        title: "dicTable.unit",
        name: "name",
        type: HardTableColumnTypeEnum.Parent,
        parent: "unit",
      },
      {
        title: "dicTable.allowedValues",
        name: "allowedEnumValuesText",
        type: HardTableColumnTypeEnum.Text,
      },
    ],
    dataCallback: this.dataCallback,
  };

  dataCallback(data: IProperty[]): IProperty[] {
    return data?.map((item) => {
      let values: string[] = [];
      if (Array.isArray(item?.enumValues)) {
        values = (item?.enumValues as IPropertyEnumValue[])
          .map((v: IPropertyEnumValue) => (typeof v == "object" ? v?.value : v))
          .filter(Boolean);
      }
      return {
        ...item,
        allowedEnumValuesText: values.join(", "),
      };
    });
  }
}
